// @flow
import './DateField.css'

import { DatePicker } from 'antd'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import * as React from 'react'

import BaseField, { type Props as FieldProps } from './BaseField'

dayjs.extend(customParseFormat)
dayjs.extend(utc)

type DateFormat =
  | 'MM/DD/YYYY'
  | 'MM/DD/YYYY HH:mm'
  | 'MM/YYYY'
  | 'YYYY-MM-DDTHH:mm:ss'
  | 'YYYY-MM-DD'
  | 'MM-YYYY'

export type Props = {
  ...FieldProps,
  id?: string,
  name?: string,
  onChange: (value: null | [Date, string]) => void,
  format?: DateFormat,
  size?: 'small' | 'middle' | 'large',
  showTime?: boolean,
  showPastPresets?: boolean,
  showFuturePresets?: boolean,
  defaultValue?: null | Date | string,
  value?: null | Date | string,
  picker?: null | 'week' | 'month' | 'quarter' | 'year',
  disabled?: boolean,
  utcValue?: boolean,
  valueParserFormat?: DateFormat,
  showPastOnly?: boolean,
  showFutureOnly?: boolean,
  minDate?: Date | string | null,
  maxDate?: Date | string | null,
  disabledGrayed?: boolean,
  showNow?: boolean,
  className?: string,
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight',
}

function DateField({
  id,
  name,
  onChange,
  format,
  size = 'large',
  showTime,
  showPastPresets,
  showFuturePresets,
  defaultValue,
  value,
  picker,
  disabled,
  utcValue,
  valueParserFormat,
  showPastOnly,
  showFutureOnly,
  minDate,
  maxDate,
  disabledGrayed = false,
  showNow = true,
  placement,
  ...rest
}: Props): React.Node {
  const [pickedValue, setPickedValue] = React.useState(null)

  /*
   * format: https://day.js.org/docs/en/display/format
   */
  const pastPresets = React.useMemo(
    () => [
      { label: 'Last 7 Days', value: dayjs().add(-7, 'd') },
      { label: 'Last 14 Days', value: dayjs().add(-14, 'd') },
      { label: 'Last 30 Days', value: dayjs().add(-30, 'd') },
      { label: 'Last 90 Days', value: dayjs().add(-90, 'd') },
    ],
    []
  )

  const futurePresets = React.useMemo(
    () => [
      { label: 'Next 7 Days', value: dayjs().add(7, 'd') },
      { label: 'Next 14 Days', value: dayjs().add(14, 'd') },
      { label: 'Next 30 Days', value: dayjs().add(30, 'd') },
      { label: 'Next 90 Days', value: dayjs().add(90, 'd') },
    ],
    []
  )

  const formatValue = React.useMemo(
    (value) => {
      if (format) {
        return format
      }

      if (picker === 'month') {
        return 'MM/YYYY'
      }

      if (showTime) {
        return 'MM/DD/YYYY hh:mm A'
      }

      return 'MM/DD/YYYY'
    },
    [format, picker, showTime]
  )

  const defaultParserFormatValue = React.useMemo(() => {
    if (valueParserFormat) {
      return valueParserFormat
    }

    if (picker === 'month') {
      return 'MM-YYYY'
    }
    if (showTime) {
      return 'YYYY-MM-DDTHH:mm:ss'
    }
    return 'YYYY-MM-DD'
  }, [picker, showTime, valueParserFormat])

  const valueIsString = React.useMemo(() => {
    if (!value) {
      return false
    }
    return typeof value === 'string'
  }, [value])

  const _minDate = React.useMemo(() => {
    if (minDate) {
      return dayjs(
        minDate,
        typeof minDate === 'string' ? valueParserFormat : undefined
      )
    }

    if (showFutureOnly) {
      if (showTime) {
        return dayjs().add(1, 'm')
      }
      if (picker === 'month') {
        return dayjs().add(1, 'M')
      }
      return dayjs().add(1, 'd')
    }
    return undefined
  }, [minDate, picker, showFutureOnly, showTime, valueParserFormat])

  const _maxDate = React.useMemo(() => {
    if (maxDate) {
      return dayjs(
        maxDate,
        typeof maxDate === 'string' ? valueParserFormat : undefined
      )
    }

    if (showPastOnly) {
      if (showTime) {
        return dayjs().add(-1, 'm')
      }
      if (picker === 'month') {
        return dayjs().add(-1, 'M')
      }
      return dayjs().add(-1, 'd')
    }
    return undefined
  }, [maxDate, picker, showPastOnly, showTime, valueParserFormat])

  const disabledTime = React.useCallback(
    (_, type) => {
      if (showTime) {
        const minTime = _minDate ? _minDate.toDate() : undefined
        const maxTime = _maxDate ? _maxDate.toDate() : undefined
        const disabledHours = []
        const disabledMinutes = []

        if (minTime && (!pickedValue || pickedValue?.isSame(_minDate, 'day'))) {
          for (let i = 0; i < minTime.getHours(); i++) {
            disabledHours.push(i === 0 ? 12 : i)
          }
          for (let i = 0; i < minTime.getMinutes(); i++) {
            disabledMinutes.push(i)
          }
        }

        if (maxTime && (!pickedValue || pickedValue?.isSame(_maxDate, 'day'))) {
          for (let i = 23; i > maxTime.getHours(); i--) {
            disabledHours.push(i === 0 ? 12 : i)
          }
          for (let i = 59; i > maxTime.getMinutes(); i--) {
            disabledMinutes.push(i)
          }
        }

        return {
          disabledHours: () => disabledHours,
          disabledMinutes: () => disabledMinutes,
        }
      }
      return undefined
    },
    [_maxDate, _minDate, pickedValue, showTime]
  )

  const styles = React.useMemo(() => {
    const _styles: {
      paddingTop: string,
      paddingBottom: string,
    } = { paddingTop: '0.438rem', paddingBottom: '0.438rem' }
    return _styles
  }, [])

  const className = React.useMemo(() => {
    let _className =
      'form-input focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] border w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5 px-2'
    if (disabledGrayed) {
      _className += ' date-disabled-no-grayed-out'
    }
    return _className
  }, [disabledGrayed])

  return (
    <BaseField {...rest}>
      <DatePicker
        use12Hours
        className={className}
        defaultValue={[
          defaultValue
            ? dayjs(
                defaultValue,
                valueIsString ? defaultParserFormatValue : undefined
              )
            : undefined,
          defaultValue
            ? dayjs(
                defaultValue,
                valueIsString ? defaultParserFormatValue : undefined
              )
            : undefined,
        ]}
        disabled={disabled}
        disabledTime={disabledTime}
        format={
          formatValue
            ? {
                format: formatValue,
                type: 'mask',
              }
            : undefined
        }
        id={id || name}
        maxDate={_maxDate}
        minDate={_minDate}
        name={name ?? 'date_field'}
        needConfirm={false}
        onChange={(date, dateString) => {
          const _date = utcValue ? date?.utc() : date
          const _dateString = _date?.format(defaultParserFormatValue)
          onChange(date ? [_date?.toDate(), _dateString] : null)
        }}
        onPickerValueChange={setPickedValue}
        picker={picker ?? undefined}
        placeholder={formatValue.toLowerCase()}
        placement={placement}
        popupStyle={{ zIndex: 10000 }}
        presets={
          showPastPresets
            ? pastPresets
            : showFuturePresets
              ? futurePresets
              : undefined
        }
        showNow={showNow}
        showTime={showTime ? { format: 'HH:mm' } : undefined}
        style={styles}
        translate="no"
        value={
          value
            ? dayjs(value, valueIsString ? defaultParserFormatValue : undefined)
            : undefined
        }
        {...rest}
      />
    </BaseField>
  )
}

export default DateField
